<template>
  <div v-if="visibleChart">
    <h4>DETTAGLIO SETTIMANA</h4>

    <Spinner v-if="loading"/>

    <div style="height:400px;" :id="chartId"></div>

    <LastAvailableDataDateLabel v-if="dateFromResponse" :date="dateFromResponse"/>
  </div>
</template>

<script>
import chartMixin from './chart';
import moment from 'moment';
import { ACTION_CONSUMPTION } from '@/store/modules/dashboard/actions';
import { capitalize } from './helpers';
import Spinner from './../ux/Spinner';
import LastAvailableDataDateLabel from './../ux/LastAvailableDataDateLabel';
import {mapState} from "vuex";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4lang_it_IT from "@amcharts/amcharts4/lang/it_IT";
import { makeid } from './helpers';

export default {
  name: 'ConsumptionGraph',

  components: { Spinner, LastAvailableDataDateLabel },

  props: {
    vector: {
      type: String,
      required: true,
    },

    date: {
      required: true,
    },

    dateFromResponse: {
      required: true,
    },
  },

  mixins: [chartMixin],

  data: () => ({
    count:0,
    chartId: makeid(),

    consumption: null,

    series: [],
    chartOptions: null,

    loading: false,
    colors: [],
    chart: undefined
  }),

  watch: {
    stateSelectedSiteId() {
      this.loadData(true);
    },

    date(newValue, oldValue) {
      // This condition for widgets that doesn't depend on any 'date' as 'Weather'
      if (!newValue || !oldValue) {
        return;
      }

      if (!newValue.isSame(oldValue, this.dateComparisonType)) {
        this.loadData();
      }
    },

    visibleChart(isVisibleChart) {
      if (isVisibleChart) {
        return;
      }

      this.$emit('it-is-not-visible');
    },
  },

  computed: {
    ...mapState({
      stateSelectedSiteId: state => state.selectedSiteId,
    }),

    visibleChart() {
      if (this.loading || this.series.length === 0) {
        return true;
      }

      const notNullSeries = Object.keys(this.series).find((seriesItemKey) =>
        this.series[seriesItemKey].data
        && this.series[seriesItemKey].data.find((seriesDataItem) => !!seriesDataItem),
      );

      return this.chartOptions && notNullSeries;
    },
  },

  mounted() {
    this.setChartOptions();

    this.loadData();
  },

  beforeDestroy() {
    this.unload();
  },

  methods: {
    async loadData(force = false) {
      if (!this.visibleChart) {
        return;
      }

      if (this.loading && !force) {
        return;
      }

      this.$emit('loading-started');

      try {
        this.loading = true;

        const { date, ...consumption } = await this.$store.dispatch(
          `dashboard/${ACTION_CONSUMPTION}`,
          {
            vector: this.vector,
            date: this.date,
          },
        );

        this.consumption = consumption;

        let currentWeekValues = this.consumption[0].values.slice(0, 168);
        let previousYearWeekValues = this.consumption[1].values.slice(0, 168);

        this.series = [
          {
            name: 'Settimana corrente',
            data: currentWeekValues.map(([, value]) => (value ? value : null)),
          },
          {
            name: 'Anno precedente',
            data: previousYearWeekValues.map(([, value]) => (value)),
          },
        ];

        if (date) {
          this.$emit('set-date-from-response', date);
        }

        this.setChartData();
      } finally {
        this.$emit('loading-finished');

        this.loading = false;
      }
    },

    setChartData() {
      let chartItem = {
        id: this.chartId,
      };
      const currentWeekValues = this.consumption[0]
      const previousYearWeekValues = this.consumption[1]
      this.makeChart(chartItem, this.chartId, currentWeekValues, previousYearWeekValues, true);
    },

    makeChart(chartItem, chartId, data, previousYearWeekValues, isComparing) {
      this.colors = this.$store.state.colors;
      let chart = am4core.create(chartId, am4charts.XYChart);
      chart.language.locale = am4lang_it_IT;

      chart.language.locale["_decimalSeparator"] = ",";
      chart.language.locale["_thousandSeparator"] = "'";
      chartItem.chart = chart;
      let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.cursorTooltipEnabled = false;
      dateAxis.groupData = true;
      dateAxis.renderer.minGridDistance = 20;
      dateAxis.renderer.labels.template.rotation = -45;
      dateAxis.renderer.labels.template.verticalCenter = "top";
      dateAxis.renderer.labels.template.horizontalCenter = "right";
      dateAxis.dateFormats.setKey("H:mm");

      chart.scrollbarX = new am4core.Scrollbar();
      chart.scrollbarX.parent = chart.bottomAxesContainer;
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.xAxis = dateAxis;
      chart.numberFormatter.numberFormat = "#.";
      chart.legend = new am4charts.Legend();
      chart.exporting.menu = new am4core.ExportMenu();
      // chart.cursor.maxTooltipDistance = 0;
      chart.series.clear();
      this.yAxis = {};
      data.mus.forEach((mu, index) => {
        if(! this.yAxis[mu]) {
          let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
          valueAxis.min = 0;
          valueAxis.name = data.columns[index + 1];
          valueAxis.title.text = `[[${mu}]]`;
          valueAxis.title.fontWeight = 600;
          valueAxis.fill = am4core.color(this.colors[index % this.colors.length]);
          valueAxis.cursorTooltipEnabled = false;
          this.yAxis[mu] = valueAxis;
        }
      });
      this.createSeries(data,chart, false, this.previousData, false);


      if (isComparing) {
        // create comparing series...
        this.createSeries(previousYearWeekValues , chart, true, data, true)
      }
    },

    createSeries(data,chart, isComparing, compareData, skipSeriesColor){
      data.sources.length=1
      data.sources.forEach((source, index) => {
        let series = chart.series.push(new am4charts.LineSeries());
        let mu = data.mus[index];
        let seriesColor = this.colors[ skipSeriesColor ? Math.floor(Math.random() * 10) : index % this.colors.length];
        series.stroke = am4core.color(seriesColor);
        series.dataFields.valueY =source;
        series.name = isComparing ? 'Anno precedente' : 'Settimana corrente'
        series.dataFields.dateX = "date";
        series.strokeWidth = 3;

        series.tooltip.label.propertyFields.fill = am4core.color('#FFFFFF');
        series.yAxis = this.yAxis[mu];
        series.connect = false;
        series.tooltip.getFillFromObject = false;
        series.tooltip.autoTextColor = false;
        series.adapter.add("tooltipText", (text, target) => {
          let tooltipData = target.tooltipDataItem.dataContext;
          var seriesData=[];
          seriesData.push(tooltipData.value.toString().split('.'));
          var finalValue = seriesData.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
          let strokeColor = target.stroke.hex;
          let dateFormat = this.getTooltipFormat(tooltipData.isComparing);
          var text = `${dateFormat}  \n [${strokeColor}]● [/] ${tooltipData.name} [bold]${finalValue}[/]`
          return text;
        });

        if(isComparing){
          series.strokeDasharray = "3,4";
        }

        series.tooltip.label.fill = am4core.color("black");
        var series_data = [];
        data.values.forEach((item, valueIndex) => {
          this.count=this.count+1;             
          if(this.count<168){
          var dataItem = {};
          dataItem.unit = data.mus[index];
          dataItem.isComparing = isComparing;
          dataItem.value =  item[index + 1] ?   item[index + 1].toFixed(2) : 0;
          dataItem.isComparing = isComparing
          dataItem.name = isComparing ? 'Anno precedente' : 'Settimana corrente';
          if(isComparing){
            let date = new Date(compareData.values[valueIndex][0]);
            dataItem.date = date;
            dataItem.previousDate = new Date(item[0]);
          } else {
            dataItem.date = new Date(item[0]);
          }
          dataItem[source] = item[index + 1];
          series_data.push(dataItem);
        }
        })
        series.data = series_data;
        this.count=0; 
      });
      this.chart = chart;
    },

    getTooltipFormat(comparing){
      return comparing ? '{previousDate.formatDate("MMM dd HH:mm - EEE")}' :'{dateX.formatDate("MMM dd HH:mm - EEE")}' ;
    },

    disableAllExceptMondays(date) {
      return date.weekday() !== 0;
    },

    setChartOptions() {
      this.chartOptions = {
        ...this.defaultChartOptions,

        tooltip: {
          x: {
            formatter: (val, { dataPointIndex }) => {
              const date = moment(this.chartOptions.xaxis.categories[dataPointIndex]).format('dddd HH:mm');

              return capitalize(date);
            },
          },
        },

        xaxis: {
          categories: [],
          labels: {
            show: true,
            rotate: -90,
            rotateAlways: true,
            hideOverlappingLabels: true,
            style: { fontSize: '7px' },// Do it smaller because we have 168 labels

            formatter: (val) => {
              return moment(val).format('HH:mm');
            },
          },
          tooltip: { enabled: false },
        },

        yaxis: {
          title: {
            text: ''
          },
          labels: {
            formatter: x => this.f(x),
          },
        },

        markers: {
          size: 0,

          hover: { size: 7 },
        },

        chart: {
          animations: {
            enabled: false
          }
        }
      };
    },

    unload() {
      if(this.chart){
        this.chart.dispose();
        this.chart = undefined;
      }
    },

    f(v) {
      return v ? this.$options.filters.formatDecimal(v) : 0;
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/Dashboard/VectorWidgets/widgets/ConsumptionGraph.vue